
import { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Paper, Grid, TextField, Button,
  Table, TableHead, TableRow, TableCell, TableBody,
  CircularProgress, Alert, Chip, Collapse, MenuItem,
  Dialog, DialogTitle, DialogContent, IconButton,
  Divider, InputAdornment
} from "@mui/material";

import LocationOnIcon    from "@mui/icons-material/LocationOn";
import ExpandMoreIcon    from "@mui/icons-material/ExpandMore";
import ExpandLessIcon    from "@mui/icons-material/ExpandLess";
import FilterListIcon    from "@mui/icons-material/FilterList";
import CloseIcon         from "@mui/icons-material/Close";
import TwoWheelerIcon    from "@mui/icons-material/TwoWheeler";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import PaymentIcon       from "@mui/icons-material/Payment";
import WarningAmberIcon  from "@mui/icons-material/WarningAmber";
import CheckCircleIcon   from "@mui/icons-material/CheckCircle";
import TrendingUpIcon    from "@mui/icons-material/TrendingUp";
import TrendingDownIcon  from "@mui/icons-material/TrendingDown";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AddIcon           from "@mui/icons-material/Add";
import SwapHorizIcon     from "@mui/icons-material/SwapHoriz";
import ReceiptLongIcon   from "@mui/icons-material/ReceiptLong";
import DownloadIcon      from "@mui/icons-material/Download";

import { profitLossService } from "../service/profitLossService";

// ─── THEME ───────────────────────────────────────────────────────────────────
const LOCATIONS = ["Khejuri", "Chaitanyapur", "Nandigram"];

const LOC_COLOR = {
  Khejuri:      { bg: "#e8f4fd", border: "#1976d2", badge: "#1976d2", light: "#bbdefb" },
  Chaitanyapur: { bg: "#f3e5f5", border: "#7b1fa2", badge: "#7b1fa2", light: "#e1bee7" },
  Nandigram:    { bg: "#e8f5e9", border: "#2e7d32", badge: "#2e7d32", light: "#c8e6c9" },
  Other:        { bg: "#fff8e1", border: "#f57f17", badge: "#f57f17", light: "#ffecb3" }
};

const Label = ({ children, color = "#777" }) => (
  <Typography variant="caption"
    sx={{ color, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", fontSize: "0.62rem" }}>
    {children}
  </Typography>
);

const Val = ({ children, color }) => (
  <Typography variant="body2" sx={{ fontWeight: 700, color: color || "#111", mt: 0.1 }}>
    {children ?? "—"}
  </Typography>
);

const InfoBlock = ({ label, value, color }) => (
  <Box>
    <Label>{label}</Label>
    <Val color={color}>{value}</Val>
  </Box>
);

const fmt     = (n) => (n ?? 0).toLocaleString("en-IN");
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN") : "—";

// ─── EXCEL EXPORT ─────────────────────────────────────────────────────────────
const exportProfitLossExcel = (data) => {
  const headers = [
    "Invoice No", "Sale Date", "Location", "SKU Code", "Model", "Color",
    "Engine Number", "Chassis Number", "Key Number",
    "Customer Name", "Father Name", "Mobile", "Address", "Village",
    "Post Office", "Police Station", "Pin Code", "Landmark",
    "ExShowroom Price", "ORP", "Sale Type", "GSTIN","Reg. No",
    "Cash Amount", "Finance Company", "Finance Amount",
    "Exchange Owner Name", "Exchange Location", "Exchange Model",
    "Exchange Reg Number", "Exchange Phone",
    "Exchange Price", "Exchange Paid", "Exchange Remaining", "Exchange Status",
    // ✅ NEW
    "Exchange Profit", "Exchange Loss Amount",
    "Due Total Amount", "Due Paid Amount", "Due Remaining", "Due Status","Exp Petrol"," Exp Legguard",
    "Exp Transport", "Exp Broker", "Exp TCR", "Exp Reg Broker", "Exp AMC/RSA", "Expense Total",
    "Helmet Qty", "Helmet Cost (FIFO)",
    "Seat Cover Qty", "Seat Cover Cost (FIFO)",
    "Bike Cover Qty", "Bike Cover Cost (FIFO)",
    "Gift Qty", "Gift Cost (FIFO)",
    "Total Accessory Cost",
    "RTO", "Insurance", "Discount", "Coupon Cashback",
    "Salesman Name", "Salesman Incentive",
    "GST Rate", "CGST", "SGST", "Taxable Amount", "Total GST Amount",
    "Purchase Price", "Total Deductions", "Effective ORP",
    "Profit / Loss (₹)", "P&L Status",
    "Remarks"
  ];

  const rows = data.map((b) => [
    b.invoiceNo || "",
    fmtDate(b.saleDate),
    b.location || "",
    b.skuCode || "",
    b.model || "",
    b.color || "",
    b.engineNumber || "",
    b.chasisNumber || "",
    b.keyNumber || "",
    b.customerName || "",
    b.fatherName || "",
    Array.isArray(b.mobile) ? b.mobile.join(", ") : (b.mobile || ""),
    b.address || "",
    b.village || "",
    b.postOffice || "",
    b.policeStation || "",
    b.pinCode || "",
    b.landmark || "",
    b.salePrice ?? 0,
    b.orp ?? 0,
    b.saleType || "",
    b.GSTIN || "",
    b.cashAmount ?? 0,
    b.finance?.companyName || "",
    b.finance?.amount ?? 0,
    b.exchange?.ownerName || "",
    b.exchange?.location || "",
    b.exchange?.model || "",
    b.exchange?.registrationNumber || "",
    b.exchange?.phone || "",
    b.exchange?.exchangePrice ?? 0,
    b.exchange?.paidAmount ?? 0,
    b.exchange?.remainingAmount ?? 0,
    b.exchange?.status || "PAID",
    // ✅ NEW — only one will be non-zero at a time
    b.exchange?.exchangeProfit ?? 0,
    b.exchange?.exchangeLossAmount ?? 0,
    b.due?.totalAmount ?? 0,
    b.due?.paidAmount ?? 0,
    b.due?.remainingAmount ?? 0,
    b.due?.status || "PAID",
    b.expenses?.petrol ?? 0,
    b.expenses?.legguard ?? 0,
    b.expenses?.transport ?? 0,
     
    b.expenses?.broker ?? 0,
    b.expenses?.tcr ?? 0,
    b.expenses?.regBroker ?? 0,
    b.expenses?.amcRsa ?? 0,
    b.expenses?.total ?? 0,
    b.accessoryDetail?.qtyHelmet ?? 0,
    b.accessoryDetail?.helmet ?? 0,
    b.accessoryDetail?.qtySeatCover ?? 0,
    b.accessoryDetail?.seatCover ?? 0,
    b.accessoryDetail?.qtyBikeCover ?? 0,
    b.accessoryDetail?.bikeCover ?? 0,
    b.accessoryDetail?.qtyGift ?? 0,
    b.accessoryDetail?.gift ?? 0,
    b.accessoryCost ?? 0,
    b.charges?.rto ?? 0,
    b.charges?.insurance ?? 0,
    b.discount ?? 0,
    b.couponCashback ?? 0,
    b.salesman?.name || "",
    b.salesmanIncentive ?? 0,
    b.tax?.gstRate ?? 0,
    b.tax?.cgst ?? 0,
    b.tax?.sgst ?? 0,
    b.tax?.taxableAmount ?? 0,
    b.tax?.totalGstAmount ?? 0,
    b.purchasePrice ?? 0,
    b.totalDeductions ?? 0,
    b.effectiveOrp ?? 0,
    b.profitLoss ?? 0,
    b.status || "",
    b.remarks || ""
  ]);

  const escape = (v) => {
    const s = String(v ?? "");
    return s.includes(",") || s.includes('"') || s.includes("\n")
      ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const csvContent = [headers, ...rows]
    .map((row) => row.map(escape).join(","))
    .join("\n");

  const BOM  = "\uFEFF";
  const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href     = url;
  link.download = `profit-loss-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

// ─── PDF EXPORT ───────────────────────────────────────────────────────────────
const exportProfitLossPDF = (data) => {
  const style = `
    <style>
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: Arial, sans-serif; font-size: 9px; color: #222; background: #fff; }
      h1 { font-size: 14px; font-weight: 800; color: #0d1b2a; margin-bottom: 4px; }
      .meta { font-size: 8px; color: #888; margin-bottom: 12px; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
      th { background: #0d1b2a; color: #fff; padding: 5px 4px; text-align: left;
           font-size: 7.5px; font-weight: 700; white-space: nowrap; }
      td { padding: 4px; border-bottom: 1px solid #eee; vertical-align: top;
           font-size: 8px; white-space: nowrap; }
      tr:nth-child(even) td { background: #f7f9ff; }
      .profit      { color: #2e7d32; font-weight: 800; }
      .loss        { color: #c62828; font-weight: 800; }
      .due         { color: #e65100; font-weight: 700; }
      .exch        { color: #1565c0; font-weight: 700; }
      .exch-profit { color: #2e7d32; font-weight: 700; }
      .exch-loss   { color: #c62828; font-weight: 700; }
      .summary { display: flex; gap: 16px; margin-bottom: 16px; flex-wrap: wrap; }
      .card { border: 1px solid #dce3f0; border-radius: 6px; padding: 8px 12px; min-width: 140px; }
      .card-label { font-size: 7px; color: #888; font-weight: 700; text-transform: uppercase; margin-bottom: 2px; }
      .card-value { font-size: 12px; font-weight: 800; }
    </style>`;

  const totalProfit = data.reduce((s, b) => s + (b.profitLoss || 0), 0);
  const totalDue    = data.reduce((s, b) => s + (b.due?.remainingAmount || 0), 0);
  const totalExch   = data.reduce((s, b) => s + (b.exchange?.remainingAmount || 0), 0);

  const summaryHtml = `
    <div class="summary">
      <div class="card">
        <div class="card-label">Grand P&L</div>
        <div class="card-value ${totalProfit >= 0 ? "profit" : "loss"}">₹${fmt(totalProfit)}</div>
      </div>
      <div class="card">
        <div class="card-label">Total Bikes</div>
        <div class="card-value" style="color:#1565c0">${data.length}</div>
      </div>
      <div class="card">
        <div class="card-label">Due Pending</div>
        <div class="card-value due">₹${fmt(totalDue)}</div>
      </div>
      <div class="card">
        <div class="card-label">Exchange Pending</div>
        <div class="card-value exch">₹${fmt(totalExch)}</div>
      </div>
    </div>`;

  // ✅ NEW cols added: Exch Profit, Exch Loss
  const cols = [
    "#", "Invoice No", "Sale Date", "Location", "Model", "Color",
    "Engine No", "Chassis No", "Customer", "Mobile", "Sale Type",
    "ExShowroom", "ORP", "Purchase Price",
    "Cash Amt", "Finance Co.", "Finance Amt",
    "Exch Owner", "Exch Model", "Exch Reg No",
    "Exch Price", "Exch Paid", "Exch Remaining", "Exch Status",
    "Exch Profit", "Exch Loss",                           // ✅ NEW
    "Due Total", "Due Paid", "Due Remaining", "Due Status","Exp Petrol","Exp Legguard",
    "Exp Transport", "Exp Broker", "Exp TCR", "Exp RegBroker", "Exp AMC/RSA", "Exp Total",
    "Helmet Qty", "Helmet Cost",
    "SeatCover Qty", "SeatCover Cost",
    "BikeCover Qty", "BikeCover Cost",
    "Gift Qty", "Gift Cost", "Acc Total",
    "RTO", "Insurance", "Discount", "Coupon CB",
    "Salesman", "Incentive",
    "GST Rate", "GST Amt",
    "Total Deductions", "Effective ORP",
    "Profit/Loss", "P&L Status", "Remarks"
  ];

  const tableRows = data.map((b, i) => {
    const isProfit    = b.status === "PROFIT";
    const exchProfit  = b.exchange?.exchangeProfit     ?? 0;
    const exchLoss    = b.exchange?.exchangeLossAmount ?? 0;
    return `<tr>
      <td>${i + 1}</td>
      <td>${b.invoiceNo || "—"}</td>
      <td>${fmtDate(b.saleDate)}</td>
      <td>${b.location || "—"}</td>
      <td>${b.model || "—"}</td>
      <td>${b.color || "—"}</td>
      <td>${b.engineNumber || "—"}</td>
      <td>${b.chasisNumber || "—"}</td>
      <td>${b.customerName || "—"}</td>
      <td>${Array.isArray(b.mobile) ? b.mobile[0] : (b.mobile || "—")}</td>
      <td>${b.saleType || "—"}</td>
      <td>₹${fmt(b.salePrice)}</td>
      <td>₹${fmt(b.orp)}</td>
      <td>₹${fmt(b.purchasePrice)}</td>
      <td>₹${fmt(b.cashAmount)}</td>
      <td>${b.finance?.companyName || "—"}</td>
      <td>₹${fmt(b.finance?.amount)}</td>
      <td>${b.exchange?.ownerName || "—"}</td>
      <td>${b.exchange?.model || "—"}</td>
      <td>${b.exchange?.registrationNumber || "—"}</td>
      <td>₹${fmt(b.exchange?.exchangePrice)}</td>
      <td>₹${fmt(b.exchange?.paidAmount)}</td>
      <td class="exch">₹${fmt(b.exchange?.remainingAmount)}</td>
      <td>${b.exchange?.status || "PAID"}</td>
      <td class="exch-profit">${exchProfit > 0 ? "₹" + fmt(exchProfit) : "—"}</td>
      <td class="exch-loss">${exchLoss   > 0 ? "₹" + fmt(exchLoss)   : "—"}</td>
      <td>₹${fmt(b.due?.totalAmount)}</td>
      <td>₹${fmt(b.due?.paidAmount)}</td>
      <td class="due">₹${fmt(b.due?.remainingAmount)}</td>
      <td>${b.due?.status || "PAID"}</td>
      <td>₹${fmt(b.expenses?.petrol)}</td>
        <td>₹${fmt(b.expenses?.legguard)}</td>
      <td>₹${fmt(b.expenses?.transport)}</td>
       
      <td>₹${fmt(b.expenses?.broker)}</td>
      <td>₹${fmt(b.expenses?.tcr)}</td>
      <td>₹${fmt(b.expenses?.regBroker)}</td>
      <td>₹${fmt(b.expenses?.amcRsa)}</td>
      <td>₹${fmt(b.expenses?.total)}</td>
      <td>${b.accessoryDetail?.qtyHelmet ?? 0}</td>
      <td>₹${fmt(b.accessoryDetail?.helmet)}</td>
      <td>${b.accessoryDetail?.qtySeatCover ?? 0}</td>
      <td>₹${fmt(b.accessoryDetail?.seatCover)}</td>
      <td>${b.accessoryDetail?.qtyBikeCover ?? 0}</td>
      <td>₹${fmt(b.accessoryDetail?.bikeCover)}</td>
      <td>${b.accessoryDetail?.qtyGift ?? 0}</td>
      <td>₹${fmt(b.accessoryDetail?.gift)}</td>
      <td>₹${fmt(b.accessoryCost)}</td>
      <td>₹${fmt(b.charges?.rto)}</td>
      <td>₹${fmt(b.charges?.insurance)}</td>
      <td>₹${fmt(b.discount)}</td>
      <td>₹${fmt(b.couponCashback)}</td>
      <td>${b.salesman?.name || "—"}</td>
      <td>₹${fmt(b.salesmanIncentive)}</td>
      <td>${b.tax?.gstRate ?? 0}%</td>
      <td>₹${fmt(b.tax?.totalGstAmount)}</td>
      <td>₹${fmt(b.totalDeductions)}</td>
      <td>₹${fmt(b.effectiveOrp)}</td>
      <td class="${isProfit ? "profit" : "loss"}">${isProfit ? "+" : ""}₹${fmt(b.profitLoss)}</td>
      <td class="${isProfit ? "profit" : "loss"}">${b.status}</td>
      <td>${b.remarks || "—"}</td>
    </tr>`;
  }).join("");

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
    <title>Profit & Loss Report</title>${style}</head>
    <body>
      <h1>Profit & Loss Report</h1>
      <div class="meta">Generated: ${new Date().toLocaleString("en-IN")} · Total Bikes: ${data.length}</div>
      ${summaryHtml}
      <table>
        <thead><tr>${cols.map((c) => `<th>${c}</th>`).join("")}</tr></thead>
        <tbody>${tableRows}</tbody>
      </table>
    </body></html>`;

  const blob = new Blob([html], { type: "text/html;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href     = url;
  link.download = `profit-loss-${new Date().toISOString().slice(0, 10)}.html`;
  link.click();
  URL.revokeObjectURL(url);
};

// ─── MAIN ─────────────────────────────────────────────────────────────────────
const ProfitLossDashboard = () => {

  const [data,        setData]        = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState("");
  const [summary,     setSummary]     = useState({});
  const [expandedLoc, setExpandedLoc] = useState({});
  const [filtersOpen, setFiltersOpen] = useState(true);

  const [detailOpen,    setDetailOpen]    = useState(false);
  const [detailData,    setDetailData]    = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [dueOpen,       setDueOpen]       = useState(false);
  const [dueSale,       setDueSale]       = useState(null);
  const [dueForm,       setDueForm]       = useState({ amount: "", date: new Date().toISOString().split("T")[0], note: "" });
  const [dueSubmitting, setDueSubmitting] = useState(false);
  const [dueError,      setDueError]      = useState("");
  const [dueSuccess,    setDueSuccess]    = useState("");

  const [filters, setFilters] = useState({
    model: "", location: "", status: "",
    fromDate: "", toDate: "", search: "", mobile: ""
  });

  const grouped = {};
  data.forEach((b) => {
    const loc = b.location || "Other";
    if (!grouped[loc]) grouped[loc] = [];
    grouped[loc].push(b);
  });

  const fetchData = useCallback(async (overrideFilters) => {
    try {
      setLoading(true);
      setError("");
      const res   = await profitLossService.getAllProfitLoss(overrideFilters ?? filters);
      const bikes = res?.data || [];
      setData(bikes);
      setSummary({
        grandProfitLoss:       res?.grandProfitLoss       ?? 0,
        grandStatus:           res?.grandStatus           ?? "PROFIT",
        grandTotalDue:         res?.grandTotalDue         ?? 0,
        totalDueBikes:         res?.totalDueBikes         ?? 0,
        grandTotalExchangeDue: res?.grandTotalExchangeDue ?? 0,
        totalExchangeDueBikes: res?.totalExchangeDueBikes ?? 0,
        totalBikes:            res?.totalBikes            ?? 0,
      });
      const exp = {};
      bikes.forEach((b) => { if (b.location) exp[b.location] = true; });
      exp["Other"] = true;
      setExpandedLoc(exp);
    } catch {
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchData(); }, []); // eslint-disable-line

  const handleChange  = (e) => setFilters((p) => ({ ...p, [e.target.name]: e.target.value }));
  const resetFilters  = () => {
    const empty = { model: "", location: "", mobile: "", status: "", fromDate: "", toDate: "", search: "" };
    setFilters(empty);
    fetchData(empty);
  };
  const toggleLocation = (loc) => setExpandedLoc((p) => ({ ...p, [loc]: !p[loc] }));

  const handleView = async (id) => {
    try {
      setDetailOpen(true);
      setDetailLoading(true);
      setDetailData(null);
      const res = await profitLossService.getSpecificProfitLoss(id);
      setDetailData(res?.data || null);
    } catch {
      setError("Failed to load details");
    } finally {
      setDetailLoading(false);
    }
  };

  const openDueModal = (bike) => {
    setDueSale(bike);
    setDueForm({ amount: "", date: new Date().toISOString().split("T")[0], note: "" });
    setDueError("");
    setDueSuccess("");
    setDueOpen(true);
  };

  const handleDueSubmit = async () => {
    if (!dueForm.amount || Number(dueForm.amount) <= 0) {
      setDueError("Please enter a valid amount");
      return;
    }
    if (Number(dueForm.amount) > (dueSale?.due?.remainingAmount ?? 0)) {
      setDueError(`Max payable: ₹${fmt(dueSale?.due?.remainingAmount)}`);
      return;
    }
    try {
      setDueSubmitting(true);
      setDueError("");
      await profitLossService.addPayment(dueSale.saleId, {
        amount: Number(dueForm.amount),
        date:   dueForm.date,
        note:   dueForm.note
      });
      setDueSuccess("Payment recorded successfully!");
      setTimeout(() => { setDueOpen(false); fetchData(); }, 1200);
    } catch (err) {
      setDueError(err?.response?.data?.message || "Payment failed");
    } finally {
      setDueSubmitting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: "1200px", mx: "auto", px: 2 }}>

      {/* ── PAGE HEADER ── */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2.5} flexWrap="wrap" gap={1}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box sx={{ bgcolor: "#0d1b2a", borderRadius: 2, p: 1, display: "flex" }}>
            <ReceiptLongIcon sx={{ color: "#fff", fontSize: 26 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={800} color="#0d1b2a" letterSpacing={-0.5}>
              Profit & Loss
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {summary.totalBikes ?? 0} bikes · Showroom-wise breakdown
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button variant="contained" color="success" size="small" startIcon={<DownloadIcon />}
            sx={{ borderRadius: 2, fontWeight: 700 }} onClick={() => exportProfitLossExcel(data)}>
            Excel / CSV
          </Button>
          <Button variant="contained" color="error" size="small" startIcon={<DownloadIcon />}
            sx={{ borderRadius: 2, fontWeight: 700 }} onClick={() => exportProfitLossPDF(data)}>
            PDF / HTML
          </Button>
          <Button startIcon={<FilterListIcon />}
            variant={filtersOpen ? "contained" : "outlined"} size="small"
            onClick={() => setFiltersOpen((p) => !p)}
            sx={{ borderRadius: 2, fontWeight: 700 }}>
            {filtersOpen ? "Hide Filters" : "Filters"}
          </Button>
        </Box>
      </Box>

      {/* ── SUMMARY CARDS ── */}
      {!loading && summary.totalBikes > 0 && (
        <Grid container spacing={1.5} mb={2.5}>
          {[
            {
              label: "Grand P&L",
              value: `₹${fmt(summary.grandProfitLoss)}`,
              color: summary.grandStatus === "PROFIT" ? "#2e7d32" : "#c62828",
              bg:    summary.grandStatus === "PROFIT" ? "#e8f5e9" : "#ffebee",
              icon:  summary.grandStatus === "PROFIT"
                ? <TrendingUpIcon sx={{ color: "#2e7d32" }} />
                : <TrendingDownIcon sx={{ color: "#c62828" }} />,
              chip: summary.grandStatus
            },
            {
              label: "Total Due Pending",
              value: `₹${fmt(summary.grandTotalDue)}`,
              color: summary.grandTotalDue > 0 ? "#e65100" : "#2e7d32",
              bg:    summary.grandTotalDue > 0 ? "#fff8f5" : "#e8f5e9",
              icon:  <WarningAmberIcon sx={{ color: summary.grandTotalDue > 0 ? "#e65100" : "#2e7d32" }} />,
              chip: `${summary.totalDueBikes} bikes`
            },
            {
              label: "Exchange Pending",
              value: `₹${fmt(summary.grandTotalExchangeDue)}`,
              color: summary.grandTotalExchangeDue > 0 ? "#1565c0" : "#2e7d32",
              bg:    summary.grandTotalExchangeDue > 0 ? "#e3f2fd" : "#e8f5e9",
              icon:  <SwapHorizIcon sx={{ color: summary.grandTotalExchangeDue > 0 ? "#1565c0" : "#2e7d32" }} />,
              chip: `${summary.totalExchangeDueBikes} bikes`
            },
            {
              label: "Total Bikes",
              value: summary.totalBikes,
              color: "#1565c0",
              bg:    "#e3f2fd",
              icon:  <TwoWheelerIcon sx={{ color: "#1565c0" }} />,
              chip: null
            }
          ].map(({ label, value, color, bg, icon, chip }) => (
            <Grid item xs={12} sm={3} key={label}>
              <Paper elevation={0} sx={{
                p: 2, borderRadius: 2.5, bgcolor: bg,
                border: `1.5px solid ${color}22`,
                display: "flex", alignItems: "center", gap: 1.5
              }}>
                <Box sx={{ bgcolor: "#fff", borderRadius: 2, p: 0.8, display: "flex", boxShadow: 1 }}>
                  {icon}
                </Box>
                <Box flex={1}>
                  <Label color="#777">{label}</Label>
                  <Typography fontWeight={800} fontSize="1.05rem" color={color}>{value}</Typography>
                </Box>
                {chip && (
                  <Chip label={chip} size="small"
                    sx={{ bgcolor: color + "22", color, fontWeight: 700, fontSize: "0.65rem" }} />
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── FILTERS ── */}
      <Collapse in={filtersOpen}>
        <Paper elevation={0} sx={{ p: 2.5, mb: 3, border: "1px solid #e0e7ef", borderRadius: 3 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={6} md={2}>
              <TextField label="Model" name="model" fullWidth size="small"
                value={filters.model} onChange={handleChange} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField select label="Location" name="location" fullWidth size="small"
                value={filters.location} onChange={handleChange}>
                <MenuItem value="">All</MenuItem>
                {LOCATIONS.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField select label="P&L Status" name="status" fullWidth size="small"
                value={filters.status} onChange={handleChange}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="PROFIT">PROFIT</MenuItem>
                <MenuItem value="LOSS">LOSS</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField label="Search" name="search" fullWidth size="small"
                value={filters.search} onChange={handleChange} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField label="Mobile" name="mobile" fullWidth size="small"
                value={filters.mobile} onChange={handleChange} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField type="date" label="From Date" name="fromDate" fullWidth size="small"
                InputLabelProps={{ shrink: true }} value={filters.fromDate} onChange={handleChange} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField type="date" label="To Date" name="toDate" fullWidth size="small"
                InputLabelProps={{ shrink: true }} value={filters.toDate} onChange={handleChange} />
            </Grid>
            <Grid item xs={6} md={2}>
              <Button fullWidth variant="contained" onClick={() => fetchData()}
                sx={{ borderRadius: 2, fontWeight: 700, bgcolor: "#0d1b2a" }}>Apply</Button>
            </Grid>
            <Grid item xs={6} md={2}>
              <Button fullWidth variant="outlined" onClick={resetFilters}
                sx={{ borderRadius: 2, fontWeight: 700 }}>Reset</Button>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {loading && <Box textAlign="center" py={6}><CircularProgress /></Box>}
      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!loading && data.length === 0 && (
        <Alert severity="info">No data found for the selected filters.</Alert>
      )}

      {/* ── SHOWROOM SECTIONS ── */}
      {!loading && Object.entries(grouped).map(([loc, bikes]) => {
        const theme  = LOC_COLOR[loc] || LOC_COLOR.Other;
        const isOpen = expandedLoc[loc] !== false;

        const locProfit      = bikes.reduce((s, b) => s + (b.profitLoss ?? 0), 0);
        const locDue         = bikes.reduce((s, b) => s + (b.due?.remainingAmount ?? 0), 0);
        const locExchangeDue = bikes.reduce((s, b) => s + (b.exchange?.remainingAmount ?? 0), 0);
        const dueCount       = bikes.filter((b) => b.hasDue).length;
        const exchangeCount  = bikes.filter((b) => b.hasExchange).length;

        return (
          <Box key={loc} mb={3}>
            {/* ── LOCATION HEADER ── */}
            <Paper elevation={0} onClick={() => toggleLocation(loc)} sx={{
              px: 2.5, py: 1.5,
              background: `linear-gradient(135deg, ${theme.bg} 0%, #fff 100%)`,
              border: `2px solid ${theme.border}`,
              borderBottom: isOpen ? "none" : `2px solid ${theme.border}`,
              borderRadius: isOpen ? "12px 12px 0 0" : "12px",
              cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              flexWrap: "wrap", gap: 1
            }}>
              <Box display="flex" alignItems="center" gap={1.5}>
                <LocationOnIcon sx={{ color: theme.badge, fontSize: 22 }} />
                <Typography variant="subtitle1" fontWeight={800} color={theme.badge} letterSpacing={0.5}>
                  {loc}
                </Typography>
                <Chip label={`${bikes.length} bikes`} size="small"
                  sx={{ bgcolor: theme.badge, color: "#fff", fontWeight: 700, fontSize: "0.7rem" }} />
              </Box>
              <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                {dueCount > 0 && (
                  <Chip icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
                    label={`${dueCount} DUE · ₹${fmt(locDue)}`} size="small"
                    sx={{ bgcolor: "#fff3e0", color: "#e65100", fontWeight: 700, border: "1px solid #ffb74d", fontSize: "0.68rem" }}
                  />
                )}
                {exchangeCount > 0 && (
                  <Chip icon={<SwapHorizIcon sx={{ fontSize: 14 }} />}
                    label={`${exchangeCount} EXCH · ₹${fmt(locExchangeDue)}`} size="small"
                    sx={{ bgcolor: "#e3f2fd", color: "#1565c0", fontWeight: 700, border: "1px solid #90caf9", fontSize: "0.68rem" }}
                  />
                )}
                <Chip
                  icon={locProfit >= 0
                    ? <TrendingUpIcon sx={{ fontSize: 14 }} />
                    : <TrendingDownIcon sx={{ fontSize: 14 }} />}
                  label={`₹${fmt(locProfit)}`} size="small"
                  sx={{
                    bgcolor: locProfit >= 0 ? "#e8f5e9" : "#ffebee",
                    color:   locProfit >= 0 ? "#2e7d32" : "#c62828",
                    border:  `1px solid ${locProfit >= 0 ? "#a5d6a7" : "#ef9a9a"}`,
                    fontWeight: 700, fontSize: "0.7rem"
                  }}
                />
                {isOpen
                  ? <ExpandLessIcon sx={{ color: theme.badge }} />
                  : <ExpandMoreIcon sx={{ color: theme.badge }} />}
              </Box>
            </Paper>

            {/* ── TABLE ── */}
            <Collapse in={isOpen}>
              <Paper elevation={0} sx={{
                border: `2px solid ${theme.border}`, borderTop: "none",
                borderRadius: "0 0 12px 12px", overflow: "hidden"
              }}>
                <Box sx={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: theme.light }}>
                        {[
                          "#", "Invoice", "Model", "Customer",
                          "Purchase", "ExShowroom", "ORP Price", "Sale Type",
                          "Expenses", "Accessories",
                          "Due Remaining",
                          "Exch Remaining",
                          "Exch Profit/Loss",   // ✅ NEW column
                          "Profit / Loss", "P&L Status", "Due Status", "Actions"
                        ].map((h) => (
                          <TableCell key={h} sx={{
                            fontWeight: 800, fontSize: "0.7rem", color: theme.badge,
                            letterSpacing: 0.4, whiteSpace: "nowrap", py: 1.2
                          }}>
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {bikes.map((b, idx) => {
                        const isProfit   = b.status === "PROFIT";
                        const isDue      = b.hasDue;
                        const isExchange = b.hasExchange;

                        // ✅ exchange profit/loss from backend
                        const exchProfit = b.exchange?.exchangeProfit     ?? 0;
                        const exchLoss   = b.exchange?.exchangeLossAmount ?? 0;

                        return (
                          <TableRow key={b.invoiceNo}
                            sx={{
                              bgcolor: isDue
                                ? "#fff8f5"
                                : idx % 2 === 0 ? "#fff" : "#fafbff",
                              "&:hover": { bgcolor: isDue ? "#fff0eb" : "#f0f4ff" },
                              transition: "background 0.15s"
                            }}>

                            <TableCell sx={{ color: "#aaa", fontSize: "0.72rem", fontWeight: 600 }}>
                              {idx + 1}
                            </TableCell>

                            <TableCell sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#1565c0", whiteSpace: "nowrap" }}>
                              {b.invoiceNo || "—"}
                              <Typography variant="caption" display="block" color="text.secondary">
                                {fmtDate(b.saleDate)}
                              </Typography>
                            </TableCell>

                            <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                              {b.model}
                              {b.color && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  {b.color}
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              <Typography variant="body2" fontWeight={700} fontSize="0.75rem">
                                {b.customerName}
                              </Typography>
                              {b.mobile && (
                                <Typography variant="caption" color="text.secondary">
                                  {Array.isArray(b.mobile) ? b.mobile[0] : b.mobile}
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                              ₹{fmt(b.purchasePrice)}
                            </TableCell>

                            <TableCell sx={{ fontWeight: 600, fontSize: "0.74rem", whiteSpace: "nowrap" }}>
                              ₹{fmt(b.salePrice)}
                            </TableCell>

                            <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                              ₹{fmt(b.orp)}
                            </TableCell>

                            <TableCell>
                              {b.saleType ? (
                                <Chip label={b.saleType} size="small"
                                  sx={{ fontWeight: 700, fontSize: "0.62rem", bgcolor: "#e3f2fd", color: "#1565c0" }} />
                              ) : "—"}
                            </TableCell>

                            <TableCell sx={{ fontSize: "0.72rem", whiteSpace: "nowrap" }}>
                              <Box>
                                <Typography variant="body2" fontWeight={700} fontSize="0.74rem">
                                  ₹{fmt(b.expenses?.total)}
                                </Typography>
                                {(b.charges?.rto > 0 || b.charges?.insurance > 0) && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    RTO+Ins ₹{fmt((b.charges?.rto ?? 0) + (b.charges?.insurance ?? 0))}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>

                            <TableCell sx={{ fontSize: "0.72rem", whiteSpace: "nowrap" }}>
                              {b.accessoryCost > 0 ? (
                                <Typography variant="body2" fontWeight={700} fontSize="0.74rem" color="#6a1b9a">
                                  ₹{fmt(b.accessoryCost)}
                                </Typography>
                              ) : <Typography variant="caption" color="#bbb">—</Typography>}
                            </TableCell>

                            {/* Due Remaining */}
                            <TableCell sx={{
                              fontWeight: 700, fontSize: "0.75rem",
                              color: isDue ? "#e65100" : "#999", whiteSpace: "nowrap"
                            }}>
                              {isDue ? `₹${fmt(b.due?.remainingAmount)}` : "—"}
                            </TableCell>

                            {/* Exchange Remaining */}
                            <TableCell sx={{
                              fontWeight: 700, fontSize: "0.75rem",
                              color: isExchange ? "#1565c0" : "#999", whiteSpace: "nowrap"
                            }}>
                              {isExchange ? (
                                <Box display="flex" alignItems="center" gap={0.4}>
                                  <SwapHorizIcon sx={{ fontSize: 13, color: "#1565c0" }} />
                                  ₹{fmt(b.exchange?.remainingAmount)}
                                </Box>
                              ) : "—"}
                            </TableCell>

                            {/* ✅ NEW — Exchange Profit / Loss column */}
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              {exchProfit > 0 ? (
                                <Chip
                                  icon={<TrendingUpIcon sx={{ fontSize: 11 }} />}
                                  label={`+₹${fmt(exchProfit)}`} size="small"
                                  sx={{
                                    fontWeight: 700, fontSize: "0.62rem",
                                    bgcolor: "#e8f5e9", color: "#2e7d32",
                                    border: "1px solid #a5d6a7"
                                  }}
                                />
                              ) : exchLoss > 0 ? (
                                <Chip
                                  icon={<TrendingDownIcon sx={{ fontSize: 11 }} />}
                                  label={`-₹${fmt(exchLoss)}`} size="small"
                                  sx={{
                                    fontWeight: 700, fontSize: "0.62rem",
                                    bgcolor: "#ffebee", color: "#c62828",
                                    border: "1px solid #ef9a9a"
                                  }}
                                />
                              ) : (
                                <Typography variant="caption" color="#ccc">—</Typography>
                              )}
                            </TableCell>

                            <TableCell sx={{
                              fontWeight: 800, fontSize: "0.78rem",
                              color: isProfit ? "#2e7d32" : "#c62828", whiteSpace: "nowrap"
                            }}>
                              {isProfit ? "+" : ""}₹{fmt(b.profitLoss)}
                            </TableCell>

                            <TableCell>
                              <Chip
                                icon={isProfit
                                  ? <TrendingUpIcon sx={{ fontSize: 12 }} />
                                  : <TrendingDownIcon sx={{ fontSize: 12 }} />}
                                label={b.status} size="small"
                                sx={{
                                  fontWeight: 700, fontSize: "0.65rem",
                                  bgcolor: isProfit ? "#e8f5e9" : "#ffebee",
                                  color:   isProfit ? "#2e7d32" : "#c62828",
                                  border:  `1px solid ${isProfit ? "#a5d6a7" : "#ef9a9a"}`
                                }}
                              />
                            </TableCell>

                            <TableCell>
                              <Chip
                                icon={isDue
                                  ? <WarningAmberIcon sx={{ fontSize: 12 }} />
                                  : <CheckCircleIcon sx={{ fontSize: 12 }} />}
                                label={b.due?.status || "PAID"} size="small"
                                sx={{
                                  fontWeight: 700, fontSize: "0.65rem",
                                  bgcolor: isDue ? "#fff3e0" : "#e8f5e9",
                                  color:   isDue ? "#e65100" : "#2e7d32",
                                  border:  `1px solid ${isDue ? "#ffb74d" : "#a5d6a7"}`
                                }}
                              />
                            </TableCell>

                            <TableCell>
                              <Box display="flex" gap={0.75} flexWrap="nowrap">
                                <Button size="small" variant="outlined"
                                  onClick={() => handleView(b.saleId)}
                                  sx={{ fontSize: "0.68rem", fontWeight: 700, borderRadius: 1.5, px: 1, py: 0.3, minWidth: 0 }}>
                                  View
                                </Button>
                                {isDue && (
                                  <Button size="small" variant="contained"
                                    startIcon={<AddIcon sx={{ fontSize: 12 }} />}
                                    onClick={() => openDueModal(b)}
                                    sx={{
                                      fontSize: "0.68rem", fontWeight: 700, borderRadius: 1.5,
                                      px: 1, py: 0.3, minWidth: 0,
                                      bgcolor: "#e65100", "&:hover": { bgcolor: "#bf360c" }
                                    }}>
                                    Due
                                  </Button>
                                )}
                              </Box>
                            </TableCell>

                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              </Paper>
            </Collapse>
          </Box>
        );
      })}

      {/* ── DETAIL MODAL ── */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} fullWidth maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
        <DialogTitle sx={{
          fontWeight: 800, bgcolor: "#0d1b2a", color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          py: 1.8, px: 2.5
        }}>
          <Box display="flex" alignItems="center" gap={1}>
            <ReceiptLongIcon />
            <Box>
              <Typography fontWeight={800} fontSize="1rem" lineHeight={1.2}>Profit & Loss Detail</Typography>
              <Typography fontSize="0.72rem" color="#90caf9">{detailData?.invoiceNo || ""}</Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setDetailOpen(false)} sx={{ color: "#fff" }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ background: "#f4f6fb", p: 2.5 }}>
          {detailLoading ? (
            <Box textAlign="center" py={6}><CircularProgress /></Box>
          ) : detailData ? (
            <Grid container spacing={2}>

              {/* 1. Sale / Bike Identity */}
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #dce3f0" }}>
                  <Typography fontWeight={800} mb={1.5} color="#1565c0"
                    display="flex" alignItems="center" gap={0.8} fontSize="0.85rem">
                    <TwoWheelerIcon fontSize="small" /> Sale & Bike Info
                  </Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={6} sm={3}><InfoBlock label="Invoice No"     value={detailData.invoiceNo}  color="#1565c0" /></Grid>
                    <Grid item xs={6} sm={3}><InfoBlock label="Sale Date"      value={fmtDate(detailData.saleDate)} /></Grid>
                    <Grid item xs={6} sm={3}><InfoBlock label="Location"       value={detailData.location} /></Grid>
                    <Grid item xs={6} sm={3}><InfoBlock label="SKU Code"       value={detailData.skuCode} /></Grid>
                    <Grid item xs={6} sm={3}><InfoBlock label="Model"          value={detailData.model} /></Grid>
                    <Grid item xs={6} sm={3}><InfoBlock label="Color"          value={detailData.color} /></Grid>
                    <Grid item xs={6} sm={3}><InfoBlock label="Engine No"      value={detailData.engineNumber} /></Grid>
                    <Grid item xs={6} sm={3}><InfoBlock label="Chassis No"     value={detailData.chasisNumber} /></Grid>
                    <Grid item xs={6} sm={3}><InfoBlock label="Key Number"     value={detailData.keyNumber} /></Grid>
                    <Grid item xs={6} sm={3}><InfoBlock label="Customer"       value={detailData.customerName} /></Grid>
                    <Grid item xs={6} sm={3}>
                      <InfoBlock label="Mobile"
                        value={Array.isArray(detailData.mobile) ? detailData.mobile.join(", ") : detailData.mobile} />
                    </Grid>
                    <Grid item xs={6} sm={3}><InfoBlock label="Father Name"    value={detailData.fatherName} /></Grid>
                    <Grid item xs={6} sm={3}><InfoBlock label="Address"        value={detailData.address} /></Grid>
                    <Grid item xs={6} sm={3}><InfoBlock label="Village"        value={detailData.village} /></Grid>
                    <Grid item xs={6} sm={3}><InfoBlock label="Post Office"    value={detailData.postOffice} /></Grid>
                    <Grid item xs={6} sm={3}><InfoBlock label="Police Station" value={detailData.policeStation} /></Grid>
                    <Grid item xs={6} sm={3}><InfoBlock label="Pin Code"       value={detailData.pinCode} /></Grid>
                    <Grid item xs={6} sm={3}><InfoBlock label="Landmark"       value={detailData.landmark} /></Grid>
                    <Grid item xs={6} sm={3}><InfoBlock label="Sale Type"      value={detailData.saleType} /></Grid>
                    <Grid item xs={6} sm={3}><InfoBlock label="GSTIN"          value={detailData.GSTIN} /></Grid>
                    <Grid item xs={6} sm={3}><InfoBlock label="Remarks"        value={detailData.remarks} /></Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* 2. Payment Mode Detail */}
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #dce3f0" }}>
                  <Typography fontWeight={800} mb={1.5} color="#0277bd" fontSize="0.85rem">
                    💳 Payment Mode
                  </Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}><InfoBlock label="ORP"          value={`₹${fmt(detailData.orp)}`}        color="#1565c0" /></Grid>
                    <Grid item xs={6}><InfoBlock label="ExShowroom"   value={`₹${fmt(detailData.salePrice)}`}  color="#1565c0" /></Grid>
                    <Grid item xs={6}><InfoBlock label="Cash Amount"  value={`₹${fmt(detailData.cashAmount)}`} /></Grid>
                    <Grid item xs={6}><InfoBlock label="Finance Co."  value={detailData.finance?.companyName || "—"} /></Grid>
                    <Grid item xs={6}><InfoBlock label="Finance Amt"  value={`₹${fmt(detailData.finance?.amount)}`} /></Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* 3. P&L Breakdown */}
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{
                  p: 2, borderRadius: 2, height: "100%",
                  border: `1.5px solid ${detailData.status === "PROFIT" ? "#a5d6a7" : "#ef9a9a"}`,
                  bgcolor: detailData.status === "PROFIT" ? "#f1f8e9" : "#fff8f8"
                }}>
                  <Typography fontWeight={800} mb={1.5} fontSize="0.85rem"
                    color={detailData.status === "PROFIT" ? "#2e7d32" : "#c62828"}
                    display="flex" alignItems="center" gap={0.8}>
                    {detailData.status === "PROFIT"
                      ? <TrendingUpIcon fontSize="small" />
                      : <TrendingDownIcon fontSize="small" />}
                    P&L Breakdown
                  </Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}><InfoBlock label="ORP"             value={`₹${fmt(detailData.orp)}`}            color="#1565c0" /></Grid>
                    <Grid item xs={6}><InfoBlock label="Effective ORP"   value={`₹${fmt(detailData.effectiveOrp)}`}   color="#0277bd" /></Grid>
                    <Grid item xs={6}><InfoBlock label="Purchase Price"  value={`₹${fmt(detailData.purchasePrice)}`}  color="#c62828" /></Grid>
                    <Grid item xs={6}><InfoBlock label="Expenses Total"  value={`₹${fmt(detailData.expenses?.total)}`} color="#e65100" /></Grid>
                    <Grid item xs={6}><InfoBlock label="Accessory Cost"  value={`₹${fmt(detailData.accessoryCost)}`}  color="#6a1b9a" /></Grid>
                    <Grid item xs={6}><InfoBlock label="RTO"             value={`₹${fmt(detailData.charges?.rto)}`} /></Grid>
                    <Grid item xs={6}><InfoBlock label="Insurance"       value={`₹${fmt(detailData.charges?.insurance)}`} /></Grid>
                    <Grid item xs={6}><InfoBlock label="Discount"        value={`₹${fmt(detailData.discount)}`}       color="#c62828" /></Grid>
                    <Grid item xs={6}><InfoBlock label="Coupon Cashback" value={`₹${fmt(detailData.couponCashback)}`} color="#c62828" /></Grid>
                    <Grid item xs={6}><InfoBlock label="Salesman Incentive" value={`₹${fmt(detailData.salesmanIncentive)}`} /></Grid>
                    {detailData.hasDue && (
                      <Grid item xs={6}>
                        <InfoBlock label="Remaining (Due)"
                          value={`₹${fmt(detailData.due?.remainingAmount)}`} color="#f04013" />
                      </Grid>
                    )}
                    {detailData.hasExchange && (
                      <Grid item xs={6}>
                        <InfoBlock label="Exch Remaining (Cost)"
                          value={`₹${fmt(detailData.exchange?.remainingAmount)}`} color="#1565c0" />
                      </Grid>
                    )}
                    {/* ✅ NEW — Exchange Profit */}
                    {(detailData.exchange?.exchangeProfit ?? 0) > 0 && (
                      <Grid item xs={6}>
                        <InfoBlock label="Exchange Profit (Saved)"
                          value={`+₹${fmt(detailData.exchange?.exchangeProfit)}`} color="#2e7d32" />
                      </Grid>
                    )}
                    {/* ✅ NEW — Exchange Loss */}
                    {(detailData.exchange?.exchangeLossAmount ?? 0) > 0 && (
                      <Grid item xs={6}>
                        <InfoBlock label="Exchange Loss (Overpaid)"
                          value={`-₹${fmt(detailData.exchange?.exchangeLossAmount)}`} color="#c62828" />
                      </Grid>
                    )}
                    <Grid item xs={12}><Divider sx={{ my: 0.5 }} /></Grid>
                    <Grid item xs={6}>
                      <InfoBlock label="Total Deductions" value={`₹${fmt(detailData.totalDeductions)}`} color="#c62828" />
                    </Grid>
                    <Grid item xs={6}>
                      <Label>Net P&L</Label>
                      <Typography fontWeight={900} fontSize="1.05rem" mt={0.2}
                        color={detailData.status === "PROFIT" ? "#2e7d32" : "#c62828"}>
                        {detailData.status === "PROFIT" ? "+" : ""}₹{fmt(detailData.profitLoss)}
                      </Typography>
                      <Chip size="small" label={detailData.status}
                        icon={detailData.status === "PROFIT"
                          ? <TrendingUpIcon sx={{ fontSize: 11 }} />
                          : <TrendingDownIcon sx={{ fontSize: 11 }} />}
                        sx={{
                          mt: 0.5, fontWeight: 800, fontSize: "0.65rem",
                          bgcolor: detailData.status === "PROFIT" ? "#e8f5e9" : "#ffebee",
                          color:   detailData.status === "PROFIT" ? "#2e7d32" : "#c62828",
                          border:  `1px solid ${detailData.status === "PROFIT" ? "#a5d6a7" : "#ef9a9a"}`
                        }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* 4. Expense Detail */}
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, height: "100%", border: "1px solid #dce3f0" }}>
                  <Typography fontWeight={800} mb={1.5} color="#00838f" fontSize="0.85rem">
                    💸 Expense Detail
                  </Typography>
                  <Grid container spacing={1.5}>
                    {[
                      ["Petrol",  detailData.expenses?.petrol],
                      ["Legguard",  detailData.expenses?.legguard],
                      ["Transport",  detailData.expenses?.transport],
                      ["Broker",     detailData.expenses?.broker],
                      ["TCR",        detailData.expenses?.tcr],
                      ["Reg Broker", detailData.expenses?.regBroker],
                      ["AMC / RSA",  detailData.expenses?.amcRsa],
                    ].map(([lbl, val]) => (
                      <Grid item xs={6} key={lbl}>
                        <InfoBlock label={lbl} value={`₹${fmt(val ?? 0)}`} color={val > 0 ? "#e65100" : "#aaa"} />
                      </Grid>
                    ))}
                    <Grid item xs={12}><Divider sx={{ my: 0.3 }} /></Grid>
                    <Grid item xs={6}>
                      <InfoBlock label="Total Expenses" value={`₹${fmt(detailData.expenses?.total)}`} color="#e65100" />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* 5. Accessory Detail (FIFO) */}
              {detailData.accessoryDetail && (
                <Grid item xs={12}>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #dce3f0" }}>
                    <Typography fontWeight={800} mb={1.5} color="#6a1b9a" fontSize="0.85rem">
                      🎁 Accessory Detail (FIFO Costing)
                    </Typography>
                    <Grid container spacing={1.5}>
                      {[
                        ["Helmet",     detailData.accessoryDetail.helmet],
                        ["Seat Cover", detailData.accessoryDetail.seatCover],
                        ["Bike Cover", detailData.accessoryDetail.bikeCover],
                        ["Gift",       detailData.accessoryDetail.gift],
                      ].map(([lbl, acc]) => (
                        <Grid item xs={12} sm={3} key={lbl}>
                          <Paper elevation={0} sx={{
                            p: 1.5, borderRadius: 1.5,
                            bgcolor: acc?.qty > 0 ? "#f3e5f5" : "#fafafa",
                            border: `1px solid ${acc?.qty > 0 ? "#ce93d8" : "#e0e0e0"}`
                          }}>
                            <Typography fontWeight={800} fontSize="0.75rem" color="#6a1b9a" mb={1}>{lbl}</Typography>
                            <Box display="flex" justifyContent="space-between" mb={0.8}>
                              <Box><Label color="#888">Qty</Label><Val>{acc?.qty ?? 0}</Val></Box>
                              <Box textAlign="right"><Label color="#888">Cost</Label><Val color="#6a1b9a">₹{fmt(acc?.cost ?? 0)}</Val></Box>
                            </Box>
                            {acc?.breakdown?.length > 0 ? (
                              <Box>
                                <Label color="#888">FIFO Batches</Label>
                                <Box mt={0.5} sx={{ borderRadius: 1, overflow: "hidden", border: "1px solid #e1bee7" }}>
                                  {acc.breakdown.map((bd, i) => (
                                    <Box key={i} display="flex" justifyContent="space-between" alignItems="center"
                                      sx={{
                                        px: 1, py: 0.5,
                                        bgcolor: i % 2 === 0 ? "#fff" : "#faf5ff",
                                        borderBottom: i < acc.breakdown.length - 1 ? "1px dashed #e1bee7" : "none"
                                      }}>
                                      <Typography variant="caption" color="text.secondary">
                                        {bd.qty} × ₹{fmt(bd.price)}{bd.fallback ? " ⚠️" : ""}
                                      </Typography>
                                      <Typography variant="caption" fontWeight={700} color="#6a1b9a">
                                        ₹{fmt(bd.subtotal)}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.disabled">No stock used</Typography>
                            )}
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                    <Box mt={1.5} display="flex" justifyContent="flex-end">
                      <Chip
                        label={`Total Accessory Cost: ₹${fmt(detailData.accessoryCost)}`} size="small"
                        sx={{ bgcolor: "#f3e5f5", color: "#6a1b9a", fontWeight: 800, border: "1px solid #ce93d8", fontSize: "0.72rem" }}
                      />
                    </Box>
                  </Paper>
                </Grid>
              )}

              {/* 6. Exchange Detail */}
              <Grid item xs={12}>
                <Paper elevation={0} sx={{
                  p: 2, borderRadius: 2,
                  border: `1px solid ${detailData.hasExchange ? "#90caf9" : "#a5d6a7"}`,
                  bgcolor: detailData.hasExchange ? "#e3f2fd" : "#f1f8e9"
                }}>
                  <Typography fontWeight={800} mb={1.5} fontSize="0.85rem"
                    color={detailData.hasExchange ? "#1565c0" : "#2e7d32"}
                    display="flex" alignItems="center" gap={0.8}>
                    <SwapHorizIcon fontSize="small" /> Exchange Vehicle
                  </Typography>

                  {detailData.exchange?.exchangePrice > 0 ? (
                    <Grid container spacing={1.5}>
                      <Grid item xs={6} sm={3}>
                        <InfoBlock label="Owner Name"       value={detailData.exchange?.ownerName || "—"} />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <InfoBlock label="Vehicle Model"    value={detailData.exchange?.model || "—"} />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <InfoBlock label="Reg Number"       value={detailData.exchange?.registrationNumber || "—"} />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <InfoBlock label="Exchange Location" value={detailData.exchange?.location || "—"} />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <InfoBlock label="Phone"            value={detailData.exchange?.phone || "—"} />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <InfoBlock label="Exchange Price"   value={`₹${fmt(detailData.exchange?.exchangePrice)}`} color="#1565c0" />
                      </Grid>

                      <Grid item xs={12}><Divider sx={{ my: 0.3 }} /></Grid>

                      <Grid item xs={6} sm={3}>
                        <Label>Status</Label>
                        <Box mt={0.3}>
                          <Chip label={detailData.exchange?.status || "PAID"} size="small"
                            icon={detailData.hasExchange
                              ? <SwapHorizIcon sx={{ fontSize: 13 }} />
                              : <CheckCircleIcon sx={{ fontSize: 13 }} />}
                            sx={{
                              fontWeight: 700, fontSize: "0.72rem",
                              bgcolor: detailData.hasExchange ? "#e3f2fd" : "#e8f5e9",
                              color:   detailData.hasExchange ? "#1565c0" : "#2e7d32",
                              border: `1px solid ${detailData.hasExchange ? "#90caf9" : "#a5d6a7"}`
                            }}
                          />
                        </Box>
                      </Grid>
                      
                      <Grid item xs={6} sm={3}>
                        <InfoBlock label="Sale price"  value={`₹${fmt(detailData.exchange?.paidAmount)}`} color="#2e7d32" />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <InfoBlock label="Profit/Loss"
                          value={`₹${fmt(detailData.exchange?.remainingAmount)}`}
                          color={detailData.hasExchange ? "#1565c0" : "#2e7d32"} />
                      </Grid>

                      {/* ✅ NEW — Exchange Profit shown in exchange section */}
                      {(detailData.exchange?.exchangeProfit ?? 0) > 0 && (
                        <Grid item xs={6} sm={3}>
                          <Paper elevation={0} sx={{ p: 1, borderRadius: 1.5, bgcolor: "#e8f5e9", border: "1px solid #a5d6a7" }}>
                            <Label color="#2e7d32">Exchange Profit</Label>
                            <Typography fontWeight={800} fontSize="0.9rem" color="#2e7d32" mt={0.3}>
                              +₹{fmt(detailData.exchange?.exchangeProfit)}
                            </Typography>
                            <Typography variant="caption" color="#555">
                              Paid less than agreed
                            </Typography>
                          </Paper>
                        </Grid>
                      )}

                      {/* ✅ NEW — Exchange Loss shown in exchange section */}
                      {(detailData.exchange?.exchangeLossAmount ?? 0) > 0 && (
                        <Grid item xs={6} sm={3}>
                          <Paper elevation={0} sx={{ p: 1, borderRadius: 1.5, bgcolor: "#ffebee", border: "1px solid #ef9a9a" }}>
                            <Label color="#c62828">Exchange Loss</Label>
                            <Typography fontWeight={800} fontSize="0.9rem" color="#c62828" mt={0.3}>
                              -₹{fmt(detailData.exchange?.exchangeLossAmount)}
                            </Typography>
                            <Typography variant="caption" color="#555">
                              Paid more than agreed
                            </Typography>
                          </Paper>
                        </Grid>
                      )}

                      {/* Payment history */}
                      {detailData.exchange?.payments?.length > 0 && (
                        <Grid item xs={12}>
                          <Label>Payment History ({detailData.exchange.payments.length} payment)</Label>
                          <Box mt={0.8} sx={{ maxHeight: 140, overflowY: "auto", borderRadius: 1.5, border: "1px solid #90caf9" }}>
                            {detailData.exchange.payments.map((p, i) => (
                              <Box key={i} display="flex" justifyContent="space-between" alignItems="center"
                                sx={{
                                  px: 1.5, py: 0.8,
                                  bgcolor: i % 2 === 0 ? "#fff" : "#f0f7ff",
                                  borderBottom: i < detailData.exchange.payments.length - 1 ? "1px dashed #90caf9" : "none"
                                }}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Box sx={{
                                    width: 20, height: 20, borderRadius: "50%", bgcolor: "#1565c0",
                                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0
                                  }}>
                                    <Typography variant="caption" color="#fff" fontWeight={800} fontSize="0.6rem">{i + 1}</Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="caption" fontWeight={700} display="block">
                                      {p.name || "—"}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {p.phone || ""}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Box textAlign="right" sx={{
                                  px: 1.5}} gap={1}>
                                  <Typography variant="caption" fontWeight={800} color="#1565c0" display="block">
                                    ₹{fmt(p.amount)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {fmtDate(p.date)}
                                  </Typography>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                      No exchange vehicle on this sale.
                    </Typography>
                  )}
                </Paper>
              </Grid>

              {/* 7. Due / Payment Status */}
              <Grid item xs={12}>
                <Paper elevation={0} sx={{
                  p: 2, borderRadius: 2,
                  border: `1px solid ${detailData.hasDue ? "#ffb74d" : "#a5d6a7"}`,
                  bgcolor: detailData.hasDue ? "#fff8f5" : "#f1f8e9"
                }}>
                  <Typography fontWeight={800} mb={1.5} fontSize="0.85rem"
                    color={detailData.hasDue ? "#e65100" : "#2e7d32"}
                    display="flex" alignItems="center" gap={0.8}>
                    <PaymentIcon fontSize="small" /> Customer Due / Payment Status
                  </Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={6} sm={3}>
                      <Label>Status</Label>
                      <Box mt={0.3}>
                        <Chip label={detailData.due?.status || "PAID"} size="small"
                          icon={detailData.hasDue
                            ? <WarningAmberIcon sx={{ fontSize: 13 }} />
                            : <CheckCircleIcon sx={{ fontSize: 13 }} />}
                          sx={{
                            fontWeight: 700, fontSize: "0.72rem",
                            bgcolor: detailData.hasDue ? "#fff3e0" : "#e8f5e9",
                            color:   detailData.hasDue ? "#e65100" : "#2e7d32",
                            border: `1px solid ${detailData.hasDue ? "#ffb74d" : "#a5d6a7"}`
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <InfoBlock label="Total Due Amount" value={`₹${fmt(detailData.due?.totalAmount)}`} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <InfoBlock label="Paid So Far"      value={`₹${fmt(detailData.due?.paidAmount)}`} color="#2e7d32" />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <InfoBlock label="Remaining"        value={`₹${fmt(detailData.due?.remainingAmount)}`}
                        color={detailData.hasDue ? "#e65100" : "#2e7d32"} />
                    </Grid>
                  </Grid>

                  {detailData.due?.payments?.length > 0 && (
                    <Box mt={2}>
                      <Label>Installment History ({detailData.due.payments.length} payments)</Label>
                      <Box mt={0.8} sx={{ maxHeight: 160, overflowY: "auto", borderRadius: 1.5, border: "1px solid #ffe0b2" }}>
                        {detailData.due.payments.map((p, i) => (
                          <Box key={i} display="flex" justifyContent="space-between" alignItems="center"
                            sx={{
                              px: 1.5, py: 0.8,
                              bgcolor: i % 2 === 0 ? "#fff" : "#fff8f5",
                              borderBottom: i < detailData.due.payments.length - 1 ? "1px dashed #ffe0b2" : "none"
                            }}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Box sx={{
                                width: 20, height: 20, borderRadius: "50%", bgcolor: "#e65100",
                                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0
                              }}>
                                <Typography variant="caption" color="#fff" fontWeight={800} fontSize="0.6rem">{i + 1}</Typography>
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                {p.note || `Payment ${i + 1}`}
                              </Typography>
                            </Box>
                            <Box textAlign="right">
                              <Typography variant="caption" fontWeight={800} color="#2e7d32" display="block">
                                ₹{fmt(p.amount)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {fmtDate(p.date)}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Paper>
              </Grid>

            </Grid>
          ) : (
            <Alert severity="info">No data found</Alert>
          )}
        </DialogContent>
      </Dialog>

      {/* ── DUE PAYMENT MODAL ── */}
      <Dialog open={dueOpen} onClose={() => !dueSubmitting && setDueOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{
          fontWeight: 800, bgcolor: "#e65100", color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "space-between"
        }}>
          <Box display="flex" alignItems="center" gap={1}><PaymentIcon /> Add Installment</Box>
          <IconButton onClick={() => setDueOpen(false)} disabled={dueSubmitting} sx={{ color: "#fff" }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5, pb: 3 }}>
          <Paper elevation={0} sx={{ p: 1.5, mb: 2.5, borderRadius: 2, bgcolor: "#fff8f5", border: "1px solid #ffb74d" }}>
            <Typography variant="body2" fontWeight={700} color="#e65100" mb={0.5}>
              {dueSale?.customerName} — {dueSale?.invoiceNo}
            </Typography>
            <Box display="flex" gap={3}>
              <Box><Label>Total Due</Label><Val>₹{fmt(dueSale?.due?.totalAmount)}</Val></Box>
              <Box><Label>Paid</Label><Val color="#2e7d32">₹{fmt(dueSale?.due?.paidAmount)}</Val></Box>
              <Box><Label>Remaining</Label><Val color="#e65100">₹{fmt(dueSale?.due?.remainingAmount)}</Val></Box>
            </Box>
          </Paper>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Payment Amount *" type="number"
                value={dueForm.amount}
                onChange={(e) => setDueForm((p) => ({ ...p, amount: e.target.value }))}
                InputProps={{ startAdornment: <InputAdornment position="start"><CurrencyRupeeIcon sx={{ fontSize: 14 }} /></InputAdornment> }}
                helperText={`Max: ₹${fmt(dueSale?.due?.remainingAmount)}`}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Payment Date *" type="date"
                value={dueForm.date}
                onChange={(e) => setDueForm((p) => ({ ...p, date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                InputProps={{ startAdornment: <InputAdornment position="start"><CalendarMonthIcon sx={{ fontSize: 14 }} /></InputAdornment> }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Note (optional)" multiline rows={2}
                value={dueForm.note}
                onChange={(e) => setDueForm((p) => ({ ...p, note: e.target.value }))}
                placeholder="e.g. Cash — 2nd instalment"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
              />
            </Grid>
          </Grid>
          {dueError   && <Alert severity="error"   sx={{ mt: 1.5, borderRadius: 2 }}>{dueError}</Alert>}
          {dueSuccess && <Alert severity="success" sx={{ mt: 1.5, borderRadius: 2 }}>{dueSuccess}</Alert>}
          <Button fullWidth variant="contained" onClick={handleDueSubmit} disabled={dueSubmitting}
            sx={{
              mt: 2.5, py: 1.2, borderRadius: 2, fontWeight: 800, fontSize: "0.9rem",
              bgcolor: dueSubmitting ? "#bdbdbd" : "#e65100",
              "&:hover": { bgcolor: "#bf360c" }
            }}>
            {dueSubmitting ? "Processing..." : "✅ Confirm Payment"}
          </Button>
        </DialogContent>
      </Dialog>

    </Box>
  );
};

export default ProfitLossDashboard;
 

